/**
 * Inbox Service for Poker Reborn
 *
 * Handles communication with the inbox service (GLID "inbox").
 * Manages player notifications, message state, and CTA orchestration.
 *
 * Follows the AccountService singleton pattern:
 * - Listens to NetworkService messages from GLID "inbox"
 * - Emits typed events via EventEmitter<InboxServiceEvents>
 * - Server returns full state on every command (no client-side diffing)
 *
 */

import { EventEmitter } from './EventEmitter';
import { networkService } from './NetworkService';
import { GLIDS } from '@/config/network';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** Call-To-Action button on an inbox message */
export interface InboxCTA {
  /** Button text (e.g., "Claim", "OK") */
  label: string;
  /** PokerTalk command (e.g., "# bonus claim"). Empty string = no action. */
  command: string;
  /** GLID to send command to (e.g., "bonus"). Empty string = no target. */
  target: string;
}

/** Single inbox message */
export interface InboxMessage {
  /** Unique message ID (e.g., "msg_1738493000_4821") */
  id: string;
  /** Message category */
  type: 'info' | 'action' | 'reward';
  /** Short title */
  title: string;
  /** Description text */
  body: string;
  /** Action button (always present) */
  cta: InboxCTA;
  /** ISO 8601 UTC timestamp */
  created_at: string;
}

/** Full inbox state returned by all inbox commands and server push */
export interface InboxState {
  /** Total messages in inbox */
  unread_count: number;
  /** All messages (newest first) */
  messages: InboxMessage[];
}

/** Events emitted by InboxService */
export interface InboxServiceEvents {
  /** Inbox state received (all commands, dismiss responses, and server push) */
  inboxUpdated: InboxState;
  /** Error from inbox service */
  error: { code: string; message: string };
}

// =============================================================================
// INBOX SERVICE
// =============================================================================

class InboxService extends EventEmitter<InboxServiceEvents> {
  private static instance: InboxService | null = null;
  private lastState: InboxState | null = null;

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): InboxService {
    if (!InboxService.instance) {
      InboxService.instance = new InboxService();
    }
    return InboxService.instance;
  }

  // ===========================================================================
  // PUBLIC API - Commands
  // ===========================================================================

  /**
   * Request full inbox state.
   * Call after authentication completes.
   * Triggers 'inboxUpdated' event with messages + count.
   */
  requestInbox(): void {
    if (!networkService.isConnected()) {
      log.inbox.warning`Cannot request inbox: not connected`;
      return;
    }
    log.inbox.info`Requesting inbox...`;
    networkService.sendCommand(GLIDS.INBOX, '# inbox');
  }

  /**
   * Dismiss (permanently remove) a single message.
   * Triggers 'inboxUpdated' event with fresh state.
   */
  dismiss(messageId: string): void {
    if (!networkService.isConnected()) return;
    networkService.sendCommand(GLIDS.INBOX, `# inbox dismiss ${messageId}`);
  }

  /**
   * Dismiss all messages, optionally filtered by source.
   * Triggers 'inboxUpdated' event with fresh state.
   */
  dismissAll(source?: string): void {
    if (!networkService.isConnected()) return;
    const command = source
      ? `# inbox dismissall ${source}`
      : '# inbox dismissall';
    networkService.sendCommand(GLIDS.INBOX, command);
  }

  /**
   * Execute a CTA action and dismiss the message.
   *
   * Client orchestration:
   * 1. If CTA has a command, send it to the target GLID
   * 2. Dismiss the message from the inbox
   *
   * The inbox server is NOT involved in CTA execution -- the command
   * goes straight to the target service. The client coordinates the
   * dismiss afterward.
   */
  executeCTA(cta: InboxCTA, messageId: string): void {
    if (!networkService.isConnected()) return;

    if (cta.command) {
      // Action/reward CTA: send command to target, then dismiss
      log.inbox.info`Executing CTA: ${cta.command} -> ${cta.target}`;
      networkService.sendCommand(cta.target, cta.command);
    } else {
      // Info/acknowledgment CTA (empty command): just dismiss
      log.inbox.info`Acknowledging message ${messageId}`;
    }

    // Dismiss after CTA (fire-and-forget, no need to await CTA result)
    this.dismiss(messageId);
  }

  /**
   * Collect All: bulk action + bulk dismiss.
   * Used for "Claim All Bonuses" style operations.
   *
   * Client orchestration:
   * 1. Send bulk command to target service (e.g., "# bonus claimall")
   * 2. Dismiss all messages from that source
   *
   * Two calls total, not N+1.
   */
  collectAll(command: string, target: string, source: string): void {
    if (!networkService.isConnected()) return;
    log.inbox.info`Collect all: ${command} -> ${target}`;
    networkService.sendCommand(target, command);
    this.dismissAll(source);
  }

  // ===========================================================================
  // PUBLIC API - State
  // ===========================================================================

  /** Get last known inbox state (null before first request) */
  getLastState(): InboxState | null {
    return this.lastState;
  }

  /** Get unread count (0 if no state yet) */
  getUnreadCount(): number {
    return this.lastState?.unread_count ?? 0;
  }

  // ===========================================================================
  // PRIVATE - Network Handlers
  // ===========================================================================

  private setupNetworkHandlers(): void {
    networkService.on('message', ({ from, body }) => {
      // Only handle messages from inbox GLID
      if (from !== GLIDS.INBOX) return;

      log.inbox.debug`Inbox message received: ${body}`;

      try {
        const message = JSON.parse(body) as Record<string, unknown>;

        switch (message.a) {
          case 'inbox':
            this.handleInboxResponse(message);
            break;

          case 'inbox_error':
            this.handleErrorResponse(message);
            break;

          default:
            log.inbox.warning`Unknown inbox action: ${message.a}`;
        }
      } catch {
        log.inbox.warning`Failed to parse inbox message: ${body}`;
      }
    });
  }

  private handleInboxResponse(message: Record<string, unknown>): void {
    const state: InboxState = {
      unread_count: Number(message.unread_count ?? 0),
      messages: (message.messages as InboxMessage[]) ?? [],
    };

    this.lastState = state;
    this.emit('inboxUpdated', state);

    log.inbox.info`Inbox updated: ${state.unread_count} messages`;
  }

  private handleErrorResponse(message: Record<string, unknown>): void {
    const errorCode = String(message.code ?? 'unknown');
    const errorMessage = String(message.message ?? 'Unknown error');

    log.inbox.warning`Inbox error: ${errorCode} - ${errorMessage}`;
    this.emit('error', { code: errorCode, message: errorMessage });
  }
}

/** Singleton inbox service instance */
export const inboxService = InboxService.getInstance();
